import { Link } from "react-router";
import { Icon, type IconName } from "./icons";
import { Card, cx } from "./ui";

/**
 * What needs an operator right now.
 *
 * An operations console that opens on totals answers "how big is the platform",
 * which nobody logs in wondering. This answers "is anything wrong", and it is
 * the first thing on the page for that reason.
 *
 * Rendering nothing when nothing is wrong is deliberate. A panel that always
 * shows something — "0 problems" — trains people to skim past the place
 * problems appear.
 */

export interface Concern {
  /** `critical` means money or availability is affected right now. */
  severity: "critical" | "warning";
  icon: IconName;
  title: string;
  detail: string;
  action?: { label: string; to: string };
}

export function Attention({ concerns }: { concerns: Concern[] }) {
  if (concerns.length === 0) return null;

  // Critical first. Two lists would be tidier and would also let someone read
  // only the first one.
  const ordered = [...concerns].sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === "critical" ? -1 : 1,
  );

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Icon name="warning" className="size-4 text-warn" />
        <h2 className="text-sm font-semibold text-fg">Needs attention</h2>
        <span className="ml-auto text-xs text-fg-subtle tabular-nums">
          {ordered.length}
        </span>
      </div>

      <ul className="divide-y divide-line">
        {ordered.map((concern) => (
          <li
            key={concern.title}
            className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3.5 gap-y-2 px-5 py-3.5 sm:grid-cols-[auto_minmax(0,1fr)_auto]"
          >
            <span
              className={cx(
                "mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg",
                concern.severity === "critical"
                  ? "bg-danger/10 text-danger"
                  : "bg-warn/10 text-warn",
              )}
            >
              <Icon name={concern.icon} className="size-3.5" />
            </span>

            <div className="min-w-0">
              <p className="text-sm font-medium text-fg">{concern.title}</p>
              <p className="mt-0.5 text-sm leading-6 text-fg-subtle">
                {concern.detail}
              </p>
            </div>

            {concern.action && (
              <Link
                to={concern.action.to}
                className="col-start-2 justify-self-start text-sm font-medium text-accent hover:underline sm:col-start-3 sm:row-start-1 sm:mt-0.5"
              >
                {concern.action.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
